import { z } from "zod";
import type { ApplicationData, OtcFollowupAnswer } from "../types";
import { ALLERGY_CATEGORIES, SELF_CARRY_MED_TYPES } from "../types";
import { HEALTH_CONDITIONS } from "../healthConditions";
import { OTC_QUESTION_FIELDS } from "../otcMedications";
import { physicalExamValidFrom } from "../config";
import {
  ADDRESS_TYPES,
  APO_OFFICES,
  APO_STATE_CODES,
  mailingAddressIssues,
  normalizeAddressType,
} from "../mailingAddress";

const yesNo = z.enum(["Yes", "No"]);
const yesNoOrNull = yesNo.nullable();

const emailField = z.string().trim().toLowerCase().email("Please enter a valid email address");

/**
 * Strict 10-digit US phone format.
 *
 * Round 22 (R-07) introduced it for `claimsPhone` (where a number missing its
 * area code, "123-4567", passed silently) and `emergencyContactPhone`; Round 23
 * extended it to the remaining four fields once testing paused, so all six
 * agreed.
 *
 * Round 24 made WHICH pattern applies depend on the application's addressType
 * rather than being fixed. This one still governs every phone on a US (or
 * back-compat absent) address; the international shape below governs the
 * APO/FPO/DPO and Foreign paths. The choice is per APPLICATION, never
 * per-field — see phoneIssues().
 */
export const PHONE_STRICT_RE = /^\d{3}-\d{3}-\d{4}$/;
export const PHONE_STRICT_MESSAGE = "Please enter the phone number as XXX-XXX-XXXX";

/**
 * Round 24 — international phone shape, used when the application's
 * addressType is APO_FPO_DPO or Foreign.
 *
 * Optional single leading "+", then digits and separators only. The period is
 * included deliberately: France, Belgium and Switzerland conventionally write
 * numbers as 01.23.45.67.89, and rejecting that would be rejecting a correctly
 * written number.
 */
export const PHONE_INTL_SHAPE_RE = /^\+?[\d\s().-]+$/;

/**
 * Digit bounds, counted with separators stripped. 15 is the E.164 maximum
 * (country code + national number); 7 is the shortest national number in real
 * use. Deliberately permissive — international numbering varies too much to
 * validate structurally, and a wrong-but-plausible number cannot be caught by
 * format rules anyway.
 */
export const PHONE_INTL_MIN_DIGITS = 7;
export const PHONE_INTL_MAX_DIGITS = 15;

export const PHONE_INTL_MESSAGE =
  "Enter a phone number with 7–15 digits. You may use +, spaces, parentheses, hyphens or periods.";

/** Example shown as the placeholder on the two non-US address paths. */
export const PHONE_INTL_PLACEHOLDER = "+44 20 7946 0958";
export const PHONE_US_PLACEHOLDER = "XXX-XXX-XXXX";

/** True for a US number in the strict XXX-XXX-XXXX shape. */
export function isStrictUsPhone(value: string): boolean {
  return PHONE_STRICT_RE.test(value.trim());
}

/**
 * True for an international number: allowed characters only, and 7-15 digits
 * once separators are stripped. This is a strict SUPERSET of isStrictUsPhone —
 * a US-format number is always acceptable on a non-US application.
 */
export function isInternationalPhone(value: string): boolean {
  const s = value.trim();
  if (!PHONE_INTL_SHAPE_RE.test(s)) return false;
  const digits = s.replace(/\D/g, "").length;
  return digits >= PHONE_INTL_MIN_DIGITS && digits <= PHONE_INTL_MAX_DIGITS;
}

/**
 * SCHEMA-LEVEL phone check — the lenient shape only.
 *
 * Round 24 moved the real rule out of the schemas and into phoneIssues() below,
 * because it is now a CROSS-FIELD rule: which pattern applies depends on
 * step2.addressType, and two of the six phone fields live in objects that
 * cannot see it (pcpPhone is on step3, claimsPhone on step4). Enforcing it
 * here for four fields and elsewhere for two would be exactly the
 * two-mechanisms drift this project keeps getting bitten by.
 *
 * What stays here is the floor every phone must clear regardless of address
 * type — it rejects letters, empty values and impossible digit counts — plus
 * requiredness. What is NOT here is the US XXX-XXX-XXXX rule. Do not "restore"
 * it here: that would reject a legitimate overseas number before phoneIssues
 * ever runs.
 */
const phoneLenient = z.string().trim().refine(isInternationalPhone, PHONE_INTL_MESSAGE);

/**
 * Optional phone: blank is fine, but anything entered must clear the same
 * floor. Used for Parent/Guardian #2, which stays optional — a half-typed
 * number there should still be caught rather than stored as-is.
 */
const phoneLenientOptional = z
  .string()
  .trim()
  .refine((v) => v === "" || isInternationalPhone(v), PHONE_INTL_MESSAGE);

const requiredString = (label: string) =>
  z.string().trim().min(1, `${label} is required`);

export interface PhoneIssue {
  /** Which step surfaces this error (matches the errors record each step reads). */
  step: number;
  /** Field key. */
  path: string;
  message: string;
}

/**
 * Every phone field in the application, with the step that renders it. Two of
 * the six do not live on step2 alongside addressType, which is the whole reason
 * this rule cannot be expressed in the per-step Zod schemas.
 */
const PHONE_FIELDS: ReadonlyArray<{
  step: number;
  path: string;
  get: (app: ApplicationData) => string;
  optional?: boolean;
}> = [
  { step: 1, path: "cellPhone", get: (a) => a.step2?.cellPhone ?? "" },
  { step: 1, path: "parent1Phone", get: (a) => a.step2?.parent1Phone ?? "" },
  { step: 1, path: "parent2Phone", get: (a) => a.step2?.parent2Phone ?? "", optional: true },
  { step: 3, path: "emergencyContactPhone", get: (a) => a.step2?.emergencyContactPhone ?? "" },
  { step: 3, path: "pcpPhone", get: (a) => a.step3?.pcpPhone ?? "" },
  { step: 4, path: "claimsPhone", get: (a) => a.step4?.claimsPhone ?? "" },
];

/**
 * Round 24 — THE phone-format rule, and the only place it lives.
 *
 * Which pattern applies depends on the application's addressType, decided once
 * for the whole application rather than per field: a family reachable only on a
 * German mobile does not have a US number for the student and a non-US one for
 * the parent. US (or absent, per the Round 22 back-compat default) keeps the
 * strict XXX-XXX-XXXX requirement exactly as before; APO/FPO/DPO and Foreign
 * get the international shape.
 *
 * MUST be called standalone, not just relied on through a schema — same Round
 * 14/22 gotcha as mailingAddressIssues(): Zod skips an object-level refinement
 * once the parse aborts on another blank required field, so on a partly-filled
 * step the rule would never surface. It is wired into every gate:
 * App.tsx's step 1/3/4 error maps, stepCompletionFlags, step1GateComplete and
 * validateFullApplication (which is both the client submit gate and the server
 * one in submission-service).
 *
 * Blank values are skipped here — requiredness belongs to the schemas, and
 * reporting "required" twice would double up the message on an empty field.
 */
export function phoneIssues(app: ApplicationData): PhoneIssue[] {
  const international = normalizeAddressType(app.step2?.addressType) !== "US";
  const issues: PhoneIssue[] = [];
  for (const field of PHONE_FIELDS) {
    const value = (field.get(app) ?? "").trim();
    if (!value) continue; // requiredness is the schema's job
    const ok = international ? isInternationalPhone(value) : isStrictUsPhone(value);
    if (!ok) {
      issues.push({
        step: field.step,
        path: field.path,
        message: international ? PHONE_INTL_MESSAGE : PHONE_STRICT_MESSAGE,
      });
    }
  }
  return issues;
}

/** Phone issues for one step, as the `errors` record shape the steps read. */
export function phoneErrorsForStep(app: ApplicationData, step: number): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of phoneIssues(app)) {
    if (issue.step === step) out[issue.path] ??= issue.message;
  }
  return out;
}

/** The placeholder to show on a phone input for this application. */
export function phonePlaceholderFor(addressType: unknown): string {
  return normalizeAddressType(addressType) === "US"
    ? PHONE_US_PLACEHOLDER
    : PHONE_INTL_PLACEHOLDER;
}

/**
 * True when the student and parent/guardian emails are both present and equal
 * after normalization. Used two ways: (1) the live, client-side message under
 * the parent-email field, and (2) the Step 2 superRefine submit/server backstop.
 *
 * It must be a standalone client-side check (not only the schema) because Zod
 * skips an object-level `.superRefine` once the object parse aborts — which
 * happens whenever any required Step 2 field is still missing. So on a
 * partly-filled form the schema rule never runs; this function does, letting the
 * UI surface the mismatch immediately on a mostly-empty step. See the inline
 * message in Step2BasicInfo and [[project-step2-email-rules]].
 */
export function studentParentEmailsMatch(studentEmail: string, parentEmail: string): boolean {
  const student = studentEmail.trim().toLowerCase();
  const parent = parentEmail.trim().toLowerCase();
  return student.length > 0 && parent.length > 0 && student === parent;
}

export const STUDENT_PARENT_EMAIL_MATCH_MESSAGE =
  "The parent/guardian email must be different from the student's email so both receive their own links and notifications.";

export const step1Schema = z.object({
  sessionYear: z
    .number({ invalid_type_error: "Session year is required" })
    .int()
    .min(2024)
    .max(2099),
  // Required attestation — Step 1 is not complete and the application cannot be
  // submitted until this is checked. (Sponsor fields used to live on Step 1; they
  // now belong to the post-submission sponsor step.)
  attestationAcknowledged: z.boolean().refine((v) => v === true, {
    message: "You must acknowledge the attestation before continuing",
  }),
});

// Round 16 — the basic-info cluster (emails, student identity, home address +
// contact, and the parent/guardian Family Information) moved from Step 2 onto
// the hard-gated Step 1 "Basic Information". The fields still physically live on
// `data.step2` (the storage shape is unchanged so the PDF/tracker keep reading
// `s2.*`); only the UI grouping and which validator drives the gate changed.
// This schema is the Step 1 required-field set — it parses `data.step2` and Zod
// ignores the eligibility/school/emergency keys it doesn't list. See
// step1GateComplete and [[project-step2-email-rules]].
export const step1BasicInfoSchema = z
  .object({
    studentEmail: emailField,
    parentEmail: emailField,
    legalFirstName: requiredString("Legal first name"),
    legalMiddleName: z.string().trim(),
    legalLastName: requiredString("Legal last name"),
    suffix: z.string().trim().max(10, "Suffix must be 10 characters or fewer"),
    preferredName: z.string().trim(),
    dob: requiredString("Date of birth"),
    ageAtProgram: z
      .number({ invalid_type_error: "Age must be a number" })
      .int()
      .min(13, "Age must be at least 13")
      .max(21, "Age must be 21 or younger")
      .nullable(),
    gender: z.enum(["Male", "Female", "Other", "Prefer not to say"]),
    // Round 22 (R-02) — the mailing-address fields are intentionally plain
    // strings here, NOT requiredString()/regex. Their requirements depend on
    // addressType (US ZIP + County rules must never fire against an APO or
    // Foreign entry), and Zod cannot express that at the object level. Every
    // per-type rule lives in mailingAddressIssues() and is applied from the
    // superRefine below plus the standalone live check in App.tsx.
    addressType: z.enum(ADDRESS_TYPES).default("US"),
    homeStreet: z.string().trim(),
    homeCity: z.string().trim(),
    homeCounty: z.string().trim(),
    homeState: z.string().trim(),
    homeZip: z.string().trim(),
    apoRecipientName: z.string().trim().default(""),
    apoUnitBox: z.string().trim().default(""),
    apoOffice: z.enum(APO_OFFICES).or(z.literal("")).default(""),
    apoStateCode: z.enum(APO_STATE_CODES).or(z.literal("")).default(""),
    apoZip: z.string().trim().default(""),
    foreignAddressLine1: z.string().trim().default(""),
    foreignAddressLine2: z.string().trim().default(""),
    foreignAddressLine3: z.string().trim().default(""),
    foreignAddressLine4: z.string().trim().default(""),
    foreignAddressLine5: z.string().trim().default(""),
    cellPhone: phoneLenient,
    parent1Name: requiredString("Parent/Guardian #1 name"),
    parent1Relationship: requiredString("Relationship"),
    parent1Phone: phoneLenient,
    parent1Email: emailField,
    parent2Name: z.string().trim(),
    parent2Relationship: z.string().trim(),
    parent2Phone: phoneLenientOptional,
    parent2Email: z.string().trim().optional().or(z.literal("")),
    tshirtSize: z.enum(["XS", "S", "M", "L", "XL", "XXL", "XXXL"], {
      errorMap: () => ({ message: "Please choose a T-shirt size" }),
    }),
  })
  .superRefine((data, ctx) => {
    // Round 22 (R-02) — per-type mailing-address rules. Same standalone-check
    // caveat as the email rule below: this only runs once the rest of Step 1
    // parses, so App.tsx also calls mailingAddressIssues() directly to surface
    // these on a partly-filled step.
    for (const issue of mailingAddressIssues(data)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [issue.path],
        message: issue.message,
      });
    }
    // The student and the parent/guardian each receive their own save-and-resume
    // magic link and confirmation/notification emails, so within one application
    // the two addresses must differ. (Distinct from the cross-applicant 409
    // one-draft-per-email guard, which is about two *different* applicants.) This
    // is the submit/server/gate backstop: it only runs once the rest of Step 1 is
    // valid (Zod skips this superRefine while the object parse aborts on a
    // partly-filled form). The live, immediate feedback for a mostly-empty step
    // comes from the same studentParentEmailsMatch helper rendered inline under
    // the parent-email field in Step1Program.
    if (studentParentEmailsMatch(data.studentEmail, data.parentEmail)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["parentEmail"],
        message: STUDENT_PARENT_EMAIL_MATCH_MESSAGE,
      });
    }
  });

// Round 16 — Step 2 is now "Eligibility": citizenship/residency and the school
// section (high school, eligibility attestations, GPA). Parses `data.step2` and
// ignores the basic-info keys it doesn't list. Its required-field set is what
// drives Step 2's 25% completeness (together with the proof + certification
// uploads).
export const step2EligibilitySchema = z
  .object({
    isUSCitizen: yesNo.nullable().refine((v) => v !== null, {
      message: "Please answer the citizenship question",
    }),
    // Residency is a conditional follow-up shown only when not a citizen. It is
    // intentionally non-blocking: a "No / No" answer surfaces a warning but does
    // not stop the applicant from continuing (admin reviews eligibility on submit).
    isUSLegalResident: yesNoOrNull,
    highSchoolName: requiredString("High school name"),
    // Graduation year is derived from the session year (session + 1) and shown
    // read-only; it is not entered by the applicant, so it is not validated here.
    graduationYear: z.number().int().nullable().optional(),
    eligibilityJunior: z.boolean(),
    eligibilitySemesterRemaining: z.boolean(),
    eligibilityNoOtherProgram: z.boolean(),
    eligibilityFullSession: z.boolean(),
    gpa: z
      .number({ invalid_type_error: "GPA must be a number" })
      .min(0, "GPA cannot be negative")
      .max(5, "GPA must be 5.0 or lower")
      .nullable(),
    classRank: z.string().trim(),
  })
  .superRefine((data, ctx) => {
    if (
      !data.eligibilityJunior ||
      !data.eligibilitySemesterRemaining ||
      !data.eligibilityNoOtherProgram ||
      !data.eligibilityFullSession
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["eligibility"],
        message: "All eligibility criteria must be confirmed before continuing.",
      });
    }
  });

// Round 16 — the Emergency Contact moved from Step 2 to Step 3 (Health History).
// The fields still live on `data.step2` (storage shape unchanged), but their
// requiredness now counts toward Step 3's completeness, not Step 2's. It is
// free-nav (NOT part of the Step 1 gate), per Round 14 still required.
export const emergencyContactSchema = z.object({
  emergencyContactName: requiredString("Emergency contact name"),
  emergencyContactRelationship: requiredString("Emergency contact relationship"),
  emergencyContactPhone: phoneLenient,
});

/**
 * The Step 1 hard gate's LOCAL condition: attestation + session year (step1)
 * AND the full basic-info required-field set (emails, identity, address,
 * Family Information — on step2). The duplicate-email check is authoritative
 * server-side and is awaited separately by the Step 1 exit handler; this
 * function is the synchronous part the UI can evaluate on every render. The
 * student≠parent rule rides along inside step1BasicInfoSchema's superRefine.
 */
export function step1GateComplete(data: ApplicationData): boolean {
  return (
    step1Schema.safeParse(data.step1).success &&
    step1BasicInfoSchema.safeParse(data.step2).success &&
    // Round 24 — the schemas only check the lenient shape; the address-type
    // dependent format lives in phoneIssues and must be applied at every gate.
    !phoneIssues(data).some((i) => i.step === 1)
  );
}

export const medicationRowSchema = z.object({
  name: z.string().trim(),
  strength: z.string().trim(),
  doseQty: z.string().trim(),
  doseForm: z.enum([
    "tablet",
    "capsule",
    "liquid",
    "inhaler",
    "injection",
    "topical",
    "drops",
    "other",
    "",
  ]),
  timesOfDay: z.array(z.enum(["morning", "noon", "evening", "prn", "other"])).default([]),
  timeOfDayOther: z.string().trim().default(""),
});

export const allergyRowSchema = z.object({
  allergy: z.string().trim(),
  reaction: z.string().trim(),
  anaphylaxisRisk: yesNoOrNull,
  bringingEpiPen: yesNoOrNull,
  // Round 12 — required per filled row (enforced in the step-3 superRefine,
  // matching the reaction/anaphylaxis/Epi-Pen pattern). "" = unanswered.
  category: z.enum(ALLERGY_CATEGORIES).or(z.literal("")),
});

const requireYesNo = (msg: string) =>
  yesNoOrNull.refine((v) => v !== null, { message: msg });

// Round 18 — OTC Medication Authorization. Per-item selections are optional
// (blank => "no" at read time); only the parent acknowledgement + signature are
// required to submit. `acknowledged` is z.boolean() (an unchecked box is a valid
// `false`) so the parent block never aborts the parse — the requirement is
// enforced in the step-3 superRefine and, for a partly-filled live form, by the
// standalone otcAcknowledgementComplete check below (the object-level superRefine
// is skipped once any OTHER required Step 3 field is still blank; see the Round
// 14 gotcha).
//
// Round 19 — the follow-up questions are now REQUIRED Yes/No selections with a
// required detail box on Yes (mirroring the Health History grid). Like the ack,
// the requirement is enforced in the step-3 superRefine + the standalone
// otcFollowupsComplete check; the schema fields stay lenient (answer nullable,
// detail an optional string) so the block never aborts the object parse.
//
// Round 20 — priorOtcReaction / doNotGive / otherNotes were removed; only
// medAllergies survives. Stored drafts may still carry the removed keys: the
// schema simply drops them (Zod objects strip unknown keys by default), the same
// way Round 19 dropped pre-Round-19 plain-string leftovers. No migration needed.
export const otcPermissionSchema = z.enum(["yes", "no", "call"]);

export const otcSelectionSchema = z.object({
  id: z.string(),
  permission: otcPermissionSchema,
});

// Round 19 — one follow-up answer (Yes/No + conditional detail). Defaulted so
// pre-Round-19 drafts (where these were plain strings) still parse: a string
// value is not an object, so `.default()` on each field supplies a blank answer.
export const otcFollowupAnswerSchema = z
  .object({
    answer: yesNoOrNull,
    detail: z.string().trim().default(""),
  })
  .default({ answer: null, detail: "" });

export const otcAuthorizationSchema = z.object({
  listVersion: z.string().default(""),
  selections: z.array(otcSelectionSchema).default([]),
  medAllergies: otcFollowupAnswerSchema,
  acknowledged: z.boolean().default(false),
  acknowledgementSignature: z.string().nullable().default(null),
  acknowledgedAtUtc: z.string().nullable().default(null),
});

// Round 20 — Sunscreen & Insect Repellent Authorization. A separate block from
// the OTC one (never nested inside it). The four give/assist permissions are
// OPTIONAL — a blank reads as "No / not authorized" at export/PDF/tracker time,
// exactly like a blank OTC per-item selection — so they are nullable here and
// carry no superRefine issue. Only the acknowledgement + signature are required,
// enforced in the step-3 superRefine AND by the standalone
// topicalsAcknowledgementComplete check below (Round 14 gotcha).
export const topicalItemPermissionsSchema = z
  .object({
    give: otcPermissionSchema.nullable().default(null),
    assist: otcPermissionSchema.nullable().default(null),
  })
  .default({ give: null, assist: null });

export const topicalsAuthorizationSchema = z.object({
  listVersion: z.string().default(""),
  sunscreen: topicalItemPermissionsSchema,
  insectRepellent: topicalItemPermissionsSchema,
  acknowledged: z.boolean().default(false),
  acknowledgementSignature: z.string().nullable().default(null),
  acknowledgedAtUtc: z.string().nullable().default(null),
});

export const OTC_ACK_REQUIRED_MESSAGE =
  "You must acknowledge and sign the Over-the-Counter Medication Authorization";

/**
 * Standalone live check for the OTC acknowledgement: true only when the box is
 * checked AND a signature is present. Exported so the Step 3 component can
 * surface the "please acknowledge" error immediately on a partly-filled form —
 * the step-3 schema's superRefine is skipped by Zod whenever another required
 * Step 3 field is still blank (Round 14 standalone-check requirement). Optional
 * chaining keeps it safe for drafts saved before Round 18.
 */
export function otcAcknowledgementComplete(
  otc: ApplicationData["step3"]["otcAuthorization"] | undefined
): boolean {
  return !!otc?.acknowledged && !!otc?.acknowledgementSignature?.trim();
}

/**
 * Round 19 — is one OTC follow-up answer complete? Answered Yes/No, and (on Yes)
 * a non-empty detail. Safe for drafts saved before this round (where the value
 * was a plain string): a non-object reads as unanswered.
 */
export function otcFollowupComplete(a: OtcFollowupAnswer | undefined): boolean {
  if (a?.answer !== "Yes" && a?.answer !== "No") return false;
  if (a.answer === "Yes" && (a.detail ?? "").trim().length === 0) return false;
  return true;
}

/**
 * Round 19 — standalone live check for the OTC follow-ups. Exported (like
 * otcAcknowledgementComplete) so the Step 3 component surfaces the error on a
 * partly-filled form even though the object-level superRefine is skipped by Zod
 * while another required Step 3 field is still blank (Round 14 gotcha).
 * Round 20 — now iterates a single-entry OTC_QUESTION_FIELDS (medAllergies).
 */
export function otcFollowupsComplete(
  otc: ApplicationData["step3"]["otcAuthorization"] | undefined
): boolean {
  return OTC_QUESTION_FIELDS.every((q) => otcFollowupComplete(otc?.[q.key]));
}

export const TOPICALS_ACK_REQUIRED_MESSAGE =
  "You must acknowledge and sign the Sunscreen & Insect Repellent Authorization";

/**
 * Round 20 — standalone live check for the topicals acknowledgement: true only
 * when the box is checked AND a signature is present. Exported for the same
 * Round 14 reason as otcAcknowledgementComplete: the step-3 superRefine is
 * skipped by Zod whenever another required Step 3 field is still blank, so the
 * component needs a check it can run itself on a partly-filled form. Optional
 * chaining keeps it safe for drafts saved before Round 20.
 */
export function topicalsAcknowledgementComplete(
  topicals: ApplicationData["step3"]["topicalsAuthorization"] | undefined
): boolean {
  return !!topicals?.acknowledged && !!topicals?.acknowledgementSignature?.trim();
}

/** A blank OTC block for the schema default (drafts predating Round 18). */
function otcAuthorizationDefault(): ApplicationData["step3"]["otcAuthorization"] {
  return {
    listVersion: "",
    selections: [],
    medAllergies: { answer: null, detail: "" },
    acknowledged: false,
    acknowledgementSignature: null,
    acknowledgedAtUtc: null,
  };
}

/** A blank topicals block for the schema default (drafts predating Round 20). */
function topicalsAuthorizationDefault(): ApplicationData["step3"]["topicalsAuthorization"] {
  return {
    listVersion: "",
    sunscreen: { give: null, assist: null },
    insectRepellent: { give: null, assist: null },
    acknowledged: false,
    acknowledgementSignature: null,
    acknowledgedAtUtc: null,
  };
}

// Round 11 — one entry per Health History grid row. Per-row requirements
// (answer required; explanation + declared extraYesNo required on Yes) are
// enforced in the step-3 superRefine, driven by the shared HEALTH_CONDITIONS
// config so the rules track the row list automatically.
export const healthConditionEntrySchema = z.object({
  answer: yesNoOrNull,
  explanation: z.string().trim(),
  extraText: z.string().trim(),
  extraYesNo: yesNoOrNull,
  extraDate: z.string().trim(),
});

export const step3HealthHistorySchema = z
  .object({
    pcpName: requiredString("Primary care physician name"),
    pcpPhone: phoneLenient,
    pcpAddress: requiredString("Primary care physician address"),
    healthConditions: z.record(healthConditionEntrySchema),
    diseases: z.object({
      chickenPox: requireYesNo("Please answer the chicken pox question"),
      germanMeasles: requireYesNo("Please answer the German measles question"),
      measles: requireYesNo("Please answer the measles question"),
      mumps: requireYesNo("Please answer the mumps question"),
    }),
    accommodationRequest: requireYesNo("Please answer the accommodation request question"),
    accommodationDescription: z.string().trim(),
    healthChangesSincePhysical: requireYesNo(
      "Please answer the health changes since physical question"
    ),
    healthChangesDescription: z.string().trim(),
    takesMedications: requireYesNo(
      "Please answer whether the student will take any medications during the session"
    ),
    medications: z.array(medicationRowSchema),
    medicationAckSignature: z.string().trim(),
    medicationAckSignatureDate: z.string().trim(),
    selfCarriesEmergencyMeds: requireYesNo(
      "Please answer whether the student will carry emergency medication on their person"
    ),
    selfCarryMedTypes: z.array(z.enum(SELF_CARRY_MED_TYPES)).default([]),
    selfCarryMedOther: z.string().trim(),
    hasAllergies: requireYesNo("Please answer whether the student has any known allergies"),
    allergies: z.array(allergyRowSchema),
    vaccinesUpToDate: requireYesNo("Please answer whether the student's vaccines are up to date"),
    vaccinesComments: z.string().trim(),
    lastPhysicalDate: requiredString("Date of last physical"),
    // Round 18 — defaulted so drafts saved before this round still parse (they
    // load unacknowledged and correctly read Incomplete until acknowledged).
    otcAuthorization: otcAuthorizationSchema.default(otcAuthorizationDefault()),
    // Round 20 — same treatment for the topicals block.
    topicalsAuthorization: topicalsAuthorizationSchema.default(topicalsAuthorizationDefault()),
  })
  .superRefine((data, ctx) => {
    // Health History grid: every row's Yes/No is required; on Yes the
    // explanation is required, as is any declared Yes/No extra (insulin pump,
    // CPAP). Text/date extras stay optional. Missing entries (old drafts)
    // count as unanswered.
    for (const spec of HEALTH_CONDITIONS) {
      const entry = data.healthConditions[spec.id];
      if (!entry || entry.answer == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["healthConditions", spec.id, "answer"],
          message: `Health history: please answer "${spec.shortLabel}"`,
        });
        continue;
      }
      if (entry.answer === "Yes") {
        if (entry.explanation.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["healthConditions", spec.id, "explanation"],
            message: `Health history: please provide details for "${spec.shortLabel}"`,
          });
        }
        if (spec.extraYesNo && entry.extraYesNo == null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["healthConditions", spec.id, "extraYesNo"],
            message: `Health history: please answer "${spec.extraYesNo.label}" for "${spec.shortLabel}"`,
          });
        }
      }
    }
    if (data.accommodationRequest === "Yes" && data.accommodationDescription.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["accommodationDescription"],
        message: "Please describe what you would like to discuss",
      });
    }
    if (data.healthChangesSincePhysical === "Yes" && data.healthChangesDescription.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["healthChangesDescription"],
        message: "Please describe the changes",
      });
    }
    const takesMeds = data.takesMedications === "Yes";
    const filledMeds = takesMeds ? data.medications.filter((m) => m.name.trim().length > 0) : [];
    if (filledMeds.length > 0) {
      if (data.medicationAckSignature.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["medicationAckSignature"],
          message: "Parent/Guardian signature is required when medications are listed",
        });
      }
      if (data.medicationAckSignatureDate.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["medicationAckSignatureDate"],
          message: "Signature date is required when medications are listed",
        });
      }
      for (const [i, med] of data.medications.entries()) {
        if (med.name.trim().length === 0) continue;
        if (!med.strength.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["medications", i, "strength"],
            message: "Strength is required",
          });
        }
        if (!med.doseQty.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["medications", i, "doseQty"],
            message: "Dose quantity is required",
          });
        }
        if (!med.doseForm) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["medications", i, "doseForm"],
            message: "Dose form is required",
          });
        }
        if (med.timesOfDay.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["medications", i, "timesOfDay"],
            message: "Select at least one time of day",
          });
        }
      }
    }
    if (data.selfCarriesEmergencyMeds === "Yes") {
      // Program medical-process rule (2026-06-07): a self-carried emergency
      // medication still requires the medication-administration form, because
      // the physician and parent authorize self-carry on it. So self-carry
      // requires takesMedications = Yes AND at least one filled medication row
      // (which in turn makes the Physician Medication Authorization upload
      // required). Surfaced near the self-carry question in Step 3.
      const hasFilledMed = takesMeds && filledMeds.length > 0;
      if (!hasFilledMed) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["selfCarryMedicationRequired"],
          message:
            "A self-carried emergency medication must also be listed in the medications table above: answer “Yes” to medications and add the medication, so the physician's authorization covers self-carry.",
        });
      }
      if (data.selfCarryMedTypes.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["selfCarryMedTypes"],
          message: "Select at least one type of emergency medication carried",
        });
      }
      if (data.selfCarryMedTypes.includes("Other") && data.selfCarryMedOther.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["selfCarryMedOther"],
          message: "Please describe the other emergency medication carried",
        });
      }
    }
    if (data.vaccinesUpToDate === "No" && data.vaccinesComments.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["vaccinesComments"],
        message: "Please explain which vaccines are not up to date",
      });
    }
    const allergyRows = data.hasAllergies === "Yes" ? data.allergies : [];
    for (const [i, allergy] of allergyRows.entries()) {
      if (allergy.allergy.trim().length === 0) continue;
      if (!allergy.reaction.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["allergies", i, "reaction"],
          message: "Reaction is required",
        });
      }
      if (allergy.anaphylaxisRisk === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["allergies", i, "anaphylaxisRisk"],
          message: "Please answer anaphylaxis risk",
        });
      }
      if (allergy.bringingEpiPen === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["allergies", i, "bringingEpiPen"],
          message: "Please answer Epi-Pen question",
        });
      }
      if (!allergy.category) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["allergies", i, "category"],
          message: "Please choose an allergy category",
        });
      }
    }
    // Round 20 — the Sunscreen & Insect Repellent acknowledgement + signature are
    // required to complete Step 3 and to submit, exactly like the OTC ack below.
    // The four give/assist permissions stay OPTIONAL (blank => "No" at read
    // time), so nothing is added for them here. NOTE: like every check in this
    // superRefine, these only run once the parse-aborting Step 3 fields are all
    // filled — the standalone topicalsAcknowledgementComplete drives the live
    // inline error on a partly-filled form (Round 14 gotcha).
    if (!data.topicalsAuthorization?.acknowledged) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["topicalsAuthorization", "acknowledged"],
        message: TOPICALS_ACK_REQUIRED_MESSAGE,
      });
    }
    if (!data.topicalsAuthorization?.acknowledgementSignature?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["topicalsAuthorization", "acknowledgementSignature"],
        message: "Signature is required for the Sunscreen & Insect Repellent Authorization",
      });
    }
    // Round 18 — the OTC acknowledgement + signature are required to complete
    // Step 3 and to submit (same requiredness as the medication acknowledgement).
    // Per-item selections and the follow-up question stay optional. These run as
    // per-field checks (not a nested object refine) so the specific errors
    // surface. NOTE: this superRefine only runs once the parse-aborting Step 3
    // fields above are all filled — the standalone otcAcknowledgementComplete
    // check drives the live inline error on a partly-filled form.
    if (!data.otcAuthorization?.acknowledged) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["otcAuthorization", "acknowledged"],
        message: OTC_ACK_REQUIRED_MESSAGE,
      });
    }
    if (!data.otcAuthorization?.acknowledgementSignature?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["otcAuthorization", "acknowledgementSignature"],
        message: "Signature is required for the Over-the-Counter Medication Authorization",
      });
    }
    // Round 19 — the OTC follow-up question(s) are required Yes/No, with a
    // required detail on Yes (same shape as the Health History grid). Per-field
    // issues so the specific control shows the error. Like the ack above, this
    // only runs once the parse-aborting Step 3 fields are filled; the standalone
    // otcFollowupsComplete drives the live inline error on a partly-filled form.
    for (const q of OTC_QUESTION_FIELDS) {
      const followup = data.otcAuthorization?.[q.key];
      if (followup?.answer !== "Yes" && followup?.answer !== "No") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["otcAuthorization", q.key, "answer"],
          message: `Please answer: ${q.label}`,
        });
        continue;
      }
      if (followup.answer === "Yes" && (followup.detail ?? "").trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["otcAuthorization", q.key, "detail"],
          message: "Please provide details for your “Yes” answer",
        });
      }
    }
  });

export const step4InsuranceSchema = z.object({
  carrierName: requiredString("Carrier name"),
  policyId: requiredString("Policy/member ID"),
  groupNumber: z.string().trim(),
  policyholderName: requiredString("Policyholder name"),
  policyholderRelationship: requiredString("Policyholder relationship"),
  policyholderDob: requiredString("Policyholder DOB"),
  claimsPhone: phoneLenient,
});

export const step5CertificationsSchema = z
  .object({
    financialResponsibilityAck: z.boolean(),
    liabilityWaiverAck: z.boolean(),
    photoReleaseAck: z.boolean(),
    photoReleaseSignature: z.string().trim(),
    codeOfConductAck: z.boolean(),
    codeOfConductSignature: z.string().trim(),
    medicalTreatmentAck: z.boolean(),
    medicalTreatmentSignature: z.string().trim(),
    parentGuardianSignature: z.string().trim(),
    parentGuardianSignatureDate: z.string().trim(),
    applicantSignature: z.string().trim(),
    applicantSignatureDate: z.string().trim(),
    finalAttestation: z.boolean(),
    // Round 17 — per-party signature attestations. Defaulted so drafts saved
    // before this round still parse (they load unacknowledged and correctly
    // read Incomplete until checked).
    applicantCertificationAck: z.boolean().default(false),
    parentCertificationAck: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    if (!data.financialResponsibilityAck) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["financialResponsibilityAck"],
        message: "You must acknowledge the financial responsibility agreement",
      });
    }
    if (!data.liabilityWaiverAck) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["liabilityWaiverAck"],
        message: "You must acknowledge the liability waiver",
      });
    }
    if (!data.photoReleaseAck) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["photoReleaseAck"],
        message: "You must acknowledge the photo/media release",
      });
    }
    if (!data.photoReleaseSignature.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["photoReleaseSignature"],
        message: "Signature required for photo/media release",
      });
    }
    if (!data.codeOfConductAck) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["codeOfConductAck"],
        message: "You must acknowledge the code of conduct",
      });
    }
    if (!data.codeOfConductSignature.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["codeOfConductSignature"],
        message: "Signature required for code of conduct",
      });
    }
    if (!data.medicalTreatmentAck) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["medicalTreatmentAck"],
        message: "You must acknowledge the authorization for medical treatment",
      });
    }
    if (!data.medicalTreatmentSignature.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["medicalTreatmentSignature"],
        message: "Parent/Guardian signature is required for the authorization for medical treatment",
      });
    }
    if (!data.parentGuardianSignature.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["parentGuardianSignature"],
        message: "Parent/Guardian signature is required",
      });
    }
    if (!data.parentGuardianSignatureDate.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["parentGuardianSignatureDate"],
        message: "Parent/Guardian signature date is required",
      });
    }
    if (!data.applicantSignature.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["applicantSignature"],
        message: "Applicant signature is required",
      });
    }
    if (!data.applicantSignatureDate.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["applicantSignatureDate"],
        message: "Applicant signature date is required",
      });
    }
    if (!data.finalAttestation) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["finalAttestation"],
        message: "You must attest that the information is true and accurate",
      });
    }
    // Round 17 — per-party signature attestations are required to submit. These
    // run as standalone per-field checks here (not an object-level refine that
    // Zod could skip): step5 has no parse-aborting required fields, so this
    // superRefine always executes, and validateFullApplication evaluates the
    // step5 schema independently of Steps 1–4 — so the gate fires even on an
    // otherwise-empty form (the Round 14 standalone-check requirement).
    if (!data.applicantCertificationAck) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["applicantCertificationAck"],
        message: "You must acknowledge the Applicant Certification",
      });
    }
    if (!data.parentCertificationAck) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["parentCertificationAck"],
        message: "You must acknowledge the Parent/Guardian Certification",
      });
    }
  });

// Sponsor Post/Unit numbers are 1–3 digit numbers (e.g. "24", "456"). Used by
// the SponsorPanel inline check and re-validated server-side at confirmation.
export const SPONSOR_POST_NUMBER_RE = /^\d{1,3}$/;
export const sponsorPostNumberSchema = z
  .string()
  .trim()
  .regex(SPONSOR_POST_NUMBER_RE, "Post/Unit number must be a 1–3 digit number.");

export interface UploadValidationContext {
  hasAsthma: boolean;
  hasMedications: boolean;
  uploads: Record<string, { storageKey: string } | undefined>;
}

export interface UploadValidationResult {
  ok: boolean;
  missing: string[];
}

export function validateRequiredUploads(ctx: UploadValidationContext): UploadValidationResult {
  const required: string[] = [
    "proof-of-residency",
    "school-certification",
    "physician-statement",
    "insurance-card-front",
    "insurance-card-back",
    "financial-responsibility",
    "liability-waiver",
  ];
  if (ctx.hasAsthma) required.push("asthma-action-plan");
  if (ctx.hasMedications) required.push("medication-authorization");
  const missing = required.filter((key) => !ctx.uploads[key]?.storageKey);
  return { ok: missing.length === 0, missing };
}

export interface RequiredUpload {
  key: string;
  label: string;
  step: 2 | 3 | 4 | 5;
}

/**
 * Single source of truth for which uploads are required and which step each one
 * belongs to. The label/step mapping matches the Step 6 "Required uploads"
 * panel; asthma-action-plan and medication-authorization are conditional on the
 * Step 3 health answers, exactly like validateRequiredUploads above. Optional
 * chaining keeps it safe for older drafts that predate these fields.
 */
export function requiredUploadsForApplication(data: ApplicationData): RequiredUpload[] {
  const hasAsthma = data.step3.healthConditions?.asthma?.answer === "Yes";
  const hasMedications =
    data.step3.takesMedications === "Yes" &&
    data.step3.medications.some((m) => m.name.trim().length > 0);
  const list: RequiredUpload[] = [
    { key: "proof-of-residency", label: "Proof of US Citizenship or Legal Residency", step: 2 },
    { key: "school-certification", label: "School Certification Form", step: 2 },
    { key: "physician-statement", label: "Physician's Statement / Physical Exam", step: 3 },
    { key: "insurance-card-front", label: "Insurance card — front", step: 4 },
    { key: "insurance-card-back", label: "Insurance card — back", step: 4 },
    {
      key: "financial-responsibility",
      label: "Agreement of Financial Responsibility (signed)",
      step: 5,
    },
    { key: "liability-waiver", label: "Liability Waiver (signed)", step: 5 },
  ];
  if (hasAsthma) list.push({ key: "asthma-action-plan", label: "Asthma Action Plan", step: 3 });
  if (hasMedications) {
    list.push({
      key: "medication-authorization",
      label: "Physician Medication Authorization",
      step: 3,
    });
  }
  return list;
}

/** The required uploads still missing on a given step (drives that step's badge,
 *  the "still needs" banner, and the completion percentage). */
export function missingUploadsForStep(data: ApplicationData, step: number): RequiredUpload[] {
  return requiredUploadsForApplication(data).filter(
    (u) => u.step === step && !data.uploads[u.key]?.storageKey
  );
}

export function physicalDateIsValid(physicalIso: string, sessionStartIso: string): boolean {
  if (!physicalIso) return false;
  const physical = new Date(physicalIso);
  if (Number.isNaN(physical.getTime())) return false;
  const earliest = physicalExamValidFrom(sessionStartIso);
  const sessionStart = new Date(sessionStartIso);
  return physical.getTime() >= earliest.getTime() && physical.getTime() <= sessionStart.getTime();
}

/**
 * Per-section completeness for Steps 1–5, keyed by step number. A section is
 * "complete" when it passes the same schema that previously gated advancing past
 * it (navigation is no longer blocked — see Change 1). Step 3 additionally
 * requires the physicalDateIsValid check, run only when a session start date is
 * supplied (it lives in config, not in ApplicationData). Never throws.
 */
export function stepCompletionFlags(
  data: ApplicationData,
  sessionStartIso?: string
): Record<1 | 2 | 3 | 4 | 5, boolean> {
  // A step is only complete once its required uploads are present too — the same
  // documents the Step 6 submission gate enforces (citizenship proof + school
  // certification on Step 2; physician statement, plus the asthma action plan
  // and medication authorization when applicable, on Step 3; both insurance
  // cards on Step 4; the signed financial-responsibility + liability waiver on
  // Step 5). Step 1 carries no uploads.
  const uploadsOk = (step: number) => missingUploadsForStep(data, step).length === 0;
  return {
    // Step 1 (Basic Information) is the hard gate and is excluded from the
    // completion %, but its badge still reflects the local gate condition.
    1: step1GateComplete(data),
    // Step 2 (Eligibility) now depends ONLY on citizenship/residency + proof and
    // school + certification — the basic-info cluster moved to Step 1.
    2: step2EligibilitySchema.safeParse(data.step2).success && uploadsOk(2),
    // Step 3 (Health History) now also owns the Emergency Contact's requiredness.
    // Round 24 — phoneIssues is applied per step here too. Without it a step
    // could read "complete" with a phone the submit gate will reject, since the
    // schemas now only enforce the lenient shape.
    3:
      step3HealthHistorySchema.safeParse(data.step3).success &&
      emergencyContactSchema.safeParse(data.step2).success &&
      !phoneIssues(data).some((i) => i.step === 3) &&
      (sessionStartIso
        ? physicalDateIsValid(data.step3.lastPhysicalDate, sessionStartIso)
        : true) &&
      uploadsOk(3),
    4:
      step4InsuranceSchema.safeParse(data.step4).success &&
      !phoneIssues(data).some((i) => i.step === 4) &&
      uploadsOk(4),
    5: step5CertificationsSchema.safeParse(data.step5).success && uploadsOk(5),
  };
}

/**
 * Completion percentage counts ONLY Sections 2, 3, 4, 5 — each worth 25%.
 * Section 1 (attestation) is excluded from the percentage but is still required
 * to submit. Step 6 (Review) and the post-submission sponsor step do not count.
 * Returns 0/25/50/75/100. Never throws.
 */
export function computeCompletionPercent(
  data: ApplicationData,
  sessionStartIso?: string
): number {
  const flags = stepCompletionFlags(data, sessionStartIso);
  return [flags[2], flags[3], flags[4], flags[5]].filter(Boolean).length * 25;
}

export interface FullApplicationValidationContext {
  sessionStartDate: string;
}

export function validateFullApplication(
  data: ApplicationData,
  ctx: FullApplicationValidationContext
): { ok: boolean; errors: Array<{ step: number; message: string; path?: string }> } {
  const errors: Array<{ step: number; message: string; path?: string }> = [];

  const s1 = step1Schema.safeParse(data.step1);
  if (!s1.success) {
    for (const e of s1.error.issues) {
      errors.push({ step: 1, message: e.message, path: e.path.join(".") });
    }
  }
  // Step 1's basic-info required fields (emails/identity/address/Family) live on
  // data.step2 but belong to Step 1 now; report them as Step 1 errors.
  const s1b = step1BasicInfoSchema.safeParse(data.step2);
  if (!s1b.success) {
    for (const e of s1b.error.issues) {
      errors.push({ step: 1, message: e.message, path: e.path.join(".") });
    }
  }
  const s2 = step2EligibilitySchema.safeParse(data.step2);
  if (!s2.success) {
    for (const e of s2.error.issues) {
      errors.push({ step: 2, message: e.message, path: e.path.join(".") });
    }
  }
  // Emergency Contact moved to Step 3 (still required for submission).
  const sEmergency = emergencyContactSchema.safeParse(data.step2);
  if (!sEmergency.success) {
    for (const e of sEmergency.error.issues) {
      errors.push({ step: 3, message: e.message, path: e.path.join(".") });
    }
  }
  const s3 = step3HealthHistorySchema.safeParse(data.step3);
  if (!s3.success) {
    for (const e of s3.error.issues) {
      errors.push({ step: 3, message: e.message, path: e.path.join(".") });
    }
  } else if (!physicalDateIsValid(data.step3.lastPhysicalDate, ctx.sessionStartDate)) {
    errors.push({
      step: 3,
      message: `Physical exam must be dated within 12 months of session start (${ctx.sessionStartDate})`,
      path: "lastPhysicalDate",
    });
  }
  const s4 = step4InsuranceSchema.safeParse(data.step4);
  if (!s4.success) {
    for (const e of s4.error.issues) {
      errors.push({ step: 4, message: e.message, path: e.path.join(".") });
    }
  }
  const s5 = step5CertificationsSchema.safeParse(data.step5);
  if (!s5.success) {
    for (const e of s5.error.issues) {
      errors.push({ step: 5, message: e.message, path: e.path.join(".") });
    }
  }
  // Round 24 — address-type dependent phone formats. This is the submit gate on
  // the client AND the server (submission-service calls the same function), so
  // it is the backstop for the whole rule: the per-step schemas above only
  // enforce the lenient shape.
  for (const issue of phoneIssues(data)) {
    errors.push({ step: issue.step, message: issue.message, path: issue.path });
  }

  // Single source of truth for the asthma flag: the Health History grid's
  // asthma row (optional chaining: stored drafts may predate the grid).
  const hasAsthma = data.step3.healthConditions?.asthma?.answer === "Yes";
  const hasMedications =
    data.step3.takesMedications === "Yes" &&
    data.step3.medications.some((m) => m.name.trim().length > 0);
  const uploadCheck = validateRequiredUploads({
    hasAsthma,
    hasMedications,
    uploads: data.uploads,
  });
  if (!uploadCheck.ok) {
    for (const missing of uploadCheck.missing) {
      errors.push({
        step: missing.startsWith("insurance") ? 4 : missing === "physician-statement" ||
          missing === "asthma-action-plan" || missing === "medication-authorization" ? 3 :
          missing === "financial-responsibility" || missing === "liability-waiver" ? 5 : 2,
        message: `Required upload missing: ${missing}`,
        path: missing,
      });
    }
  }

  return { ok: errors.length === 0, errors };
}
